import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { AgAutocomplete, AgText } from 'src/components/ui';
import { cn } from 'src/components/ui/utils';
import { LedgerAccountDetails, useLedgerAccounts } from 'src/modules/accounting/useLedgerAccounts';
import { LedgerAccountType } from 'types/graphql';

const accountOrder = [
  'CashAssets',
  'CurrentAssets',
  'LongTermAssets',
  'OtherAssets',
  'CurrentLiabilities',
  'LongTermLiabilities',
  'Equity',
  'Income',
  'OtherIncome',
  'DirectCosts',
  'IndirectCosts',
  'OtherCosts',
];

const accountToLabelMap: Record<string, 'Asset' | 'Liability' | 'Income' | 'Expense'> = {
  CashAssets: 'Asset',
  CurrentAssets: 'Asset',
  LongTermAssets: 'Asset',
  OtherAssets: 'Asset',
  CurrentLiabilities: 'Liability',
  LongTermLiabilities: 'Liability',
  Equity: 'Liability',
  Income: 'Income',
  OtherIncome: 'Income',
  DirectCosts: 'Expense',
  IndirectCosts: 'Expense',
  OtherCosts: 'Expense',
};

const typeColorClasses = {
  Asset: 'bg-blue-bg text-blue-text',
  Liability: 'bg-orange-bg text-orange-text',
  Income: 'bg-green-bg text-green-text',
  Expense: 'bg-red-bg text-red-text',
};

function AccountTypeBadge({ type }: { type?: LedgerAccountType }) {
  if (!type) return null;
  const accountType = accountToLabelMap[type];
  if (!accountType) return null;

  return (
    <span className={cn('px-2 py-1 rounded-full text-12 font-medium', typeColorClasses[accountType])}>
      {accountType}
    </span>
  );
}

type Props = {
  /**
   * Filter accounts by type. If not provided, all accounts are shown.
   * Can pass an array of account types to show multiple types.
   */
  accountTypes?: LedgerAccountType[];
  /**
   * Optional override for options. If provided, accountTypes filter is ignored.
   */
  options?: LedgerAccountDetails[];
  /**
   * Custom label for the autocomplete field. Defaults to "Account".
   */
  label?: string;
  /**
   * The currently selected value
   */
  value?: LedgerAccountDetails | null;
  /**
   * Callback when selection changes - uses MUI-compatible signature for backward compatibility
   */
  onChange?: (event: unknown, value: LedgerAccountDetails | null) => void;
  /**
   * Size variant
   */
  size?: 'small' | 'medium';
  /**
   * Whether the field is disabled
   */
  disabled?: boolean;
  /**
   * Suggested value shown with reduced opacity
   */
  suggestedValue?: LedgerAccountDetails;
  /**
   * Additional class name
   */
  className?: string;
};

export default function LedgerAutocomplete({
  accountTypes,
  options: optionsProp,
  label = 'Account',
  value,
  onChange,
  size = 'medium',
  disabled,
  suggestedValue,
  className,
}: Props) {
  const flags = useFlags();
  const allAccounts = useLedgerAccounts();

  const filteredOptions = useMemo(() => {
    // If explicit options passed, use those
    if (optionsProp?.length) {
      return optionsProp;
    }

    // Otherwise use hook data with optional type filtering
    if (!allAccounts?.length) {
      return [];
    }

    if (accountTypes?.length) {
      return allAccounts.filter((account) => accountTypes.includes(account.accountType));
    }

    return allAccounts;
  }, [optionsProp, allAccounts, accountTypes]);

  const sortedOptions = useMemo(() => {
    if (!filteredOptions?.length) {
      return [];
    }

    return [...filteredOptions].sort(
      (a, b) => accountOrder.indexOf(a.accountType) - accountOrder.indexOf(b.accountType)
    );
  }, [filteredOptions]);

  if (!flags.accounting) {
    return null;
  }

  return (
    <AgAutocomplete<LedgerAccountDetails>
      className={className}
      label={label}
      options={sortedOptions}
      value={value}
      onChange={(newValue) => onChange?.(null, newValue)}
      getOptionLabel={(option) => option.name || ''}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      size={size === 'small' ? 'sm' : 'md'}
      isDisabled={disabled}
      suggestedValue={suggestedValue}
      endContent={<AccountTypeBadge type={value?.accountType} />}
      renderOption={(option) => (
        <div className="flex flex-col w-full gap-2">
          <div className="flex justify-between items-center">
            <span>{option.name}</span>
            <AccountTypeBadge type={option.accountType} />
          </div>
          <AgText size="12" color="secondary">
            {option.number}
          </AgText>
        </div>
      )}
    />
  );
}
